import { useEffect } from 'react';

interface UseAutoCopyProps {
  shortUrl: string | undefined;
  selectAndCopy: () => void;
  transitionTime: number;
}

export const useAutoCopy = ({
  shortUrl,
  selectAndCopy,
  transitionTime,
}: UseAutoCopyProps): void => {
  useEffect(() => {
    if (typeof shortUrl === 'undefined') {
      return undefined;
    }

    let timeout: NodeJS.Timeout | undefined;

    // NOTE: copying is allowed only in (trusted) user interaction handler
    // we need to wait for URL to come from the server, so the best we can do
    // is to trigger the process on ANY interaction made after the request
    // is finished; on desktop (with the mouse/trackpad) the mousemove will work
    // smoothly; on mobile or with keyboard/voice navigation clicking is still
    // an option
    function userInteractionHandler() {
      // NOTE: wait for URL to be fully visible before positioning the tooltip
      const urlVisibleMaximalTimeout = transitionTime * 2;
      timeout = setTimeout(selectAndCopy, urlVisibleMaximalTimeout);

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      detachUserInteractionHandler();
    }

    function detachUserInteractionHandler() {
      document.removeEventListener('mousemove', userInteractionHandler, false);
    }

    document.addEventListener('mousemove', userInteractionHandler, false);

    return () => {
      if (typeof timeout !== 'undefined') {
        clearTimeout(timeout);
      }

      detachUserInteractionHandler();
    };
  }, [shortUrl, selectAndCopy, transitionTime]);
};
